import { memo } from "react";

import Icon from "@/components/Icon/Icon";
import { Button } from "@/components/ui/button";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { useForm } from "../../hooks/useForm";

interface DynamicCollectionAddButtonProps {
  /** 読み取り専用モード */
  readOnly?: boolean;
  /** データテスト用ID */
  "data-testid"?: string;
  /** 追加のクラス名 */
  className?: string;
  /** クリック時のコールバック（外部制御） */
  onClick?: () => void;
}

/**
 * 動的コレクション追加ボタンコンポーネント
 * 動的コレクションを追加するためのボタンを表示します。
 *
 * 外部からonClickを指定して制御することも、
 * 内部的にuseFormからdynamicCollections.addCollectionを使用することもできます。
 */
export const DynamicCollectionAddButton = memo(
  ({
    readOnly,
    className,
    onClick,
    "data-testid": testId = "add-dynamic-collection-button",
  }: DynamicCollectionAddButtonProps) => {
    const { t } = useTranslation(defaultNS);
    const form = useForm();

    // 動的コレクション情報を取得
    const dynamicCollections = form.getDynamicCollections?.();

    // 追加できない場合（読み取り専用、または動的コレクション追加不可）は表示しない
    if (readOnly || (!onClick && !dynamicCollections?.canAdd)) {
      return null;
    }

    // 優先順位: 外部制御のonClickがあればそれを使用、なければ内部のaddCollectionを使用
    const handleClick = onClick || dynamicCollections?.addCollection;

    return (
      <div className={`flex justify-end mt-6 ${className || ""}`}>
        <Button
          variant="outline"
          size="sm"
          onClick={handleClick}
          className="transition-transform active:scale-95"
          data-testid={testId}
        >
          <Icon iconName="PlusIcon" className="mr-2" />
          {t("common.action.add")}
        </Button>
      </div>
    );
  },
);

DynamicCollectionAddButton.displayName = "DynamicCollectionAddButton";
